import type { ReactNode } from 'react'
import React, { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import type { CoverageInformationItem } from '@flows/index'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { Popup } from '@ui-kit/components/Popup'
import { body1 } from '@ui-kit/core/typography'

import { CoverageCmsIcon } from './CoverageCmsIcon'

const styles = tv({
  slots: {
    container: 'flex-col gap-8',
    item: 'flex-row items-center gap-4',
    icon: 'h-6 w-6 shrink-0',
    content: 'flex-1 flex-col',
    title: body1({ bold: true }),
    chevron: 'h-6 w-6 shrink-0',
  },
})

interface Props {
  items: CoverageInformationItem[]
  renderContent?: (item: CoverageInformationItem) => ReactNode
}

export const CoverageInformation = ({ items, renderContent }: Props) => {
  const { container, item: itemStyle, icon, content, title, chevron } = styles()
  const [openItemKey, setOpenItemKey] = useState<string | null>(null)

  if (items.length === 0) {
    return null
  }

  const openItem = items.find((item) => item._key === openItemKey)
  const openItemContent = openItem ? renderContent?.(openItem) : undefined

  return (
    <View className={container()}>
      {items.map((item) => {
        const row = (
          <View className={itemStyle()}>
            <View className={icon()}>
              <CoverageCmsIcon icon={item.icon} />
            </View>
            <View className={content()}>
              <Text className={title()}>{item.title}</Text>
            </View>
            <View className={chevron()}>
              <PhosphorIcon name="CaretRight" size={24} />
            </View>
          </View>
        )

        if (!renderContent?.(item)) {
          return (
            <View key={item._key} testID={`coverage-info-item-${item._key}`}>
              {row}
            </View>
          )
        }

        return (
          <Pressable
            key={item._key}
            testID={`coverage-info-item-${item._key}`}
            accessibilityRole="button"
            onPress={() => setOpenItemKey(item._key)}
          >
            {row}
          </Pressable>
        )
      })}
      <Popup
        visible={openItemKey !== null}
        onRequestClose={() => setOpenItemKey(null)}
        isFullHeight
      >
        <ScrollView>{openItemContent}</ScrollView>
      </Popup>
    </View>
  )
}
